import React from 'react';
import PropTypes from 'prop-types';
import TimeAxisTickLabel from 'components/time-axis-tick-label';
import * as LayoutMode from 'constants/layout-mode';
import * as Orient from 'constants/orient';
import {getTranslate} from 'utils/attribute-util';
import {getFullWidthX} from 'utils/layout-util';
import {getTimeTicks} from 'utils/tick-util';
import styles from './index.css';
import {mean, identity, debounce} from 'lodash';
import ResizeObserver from 'resize-observer-polyfill';

export default class TimeAxis extends React.Component {
  static propTypes = {
    data: PropTypes.array,
    domain: PropTypes.array,
    height: PropTypes.number,
    layoutMode: PropTypes.oneOf(Object.values(LayoutMode)),
    numTicks: PropTypes.number,
    orient: PropTypes.oneOf([Orient.BOTTOM, Orient.TOP]),
    tickPadding: PropTypes.number,
    tickWidth: PropTypes.number,
    width: PropTypes.number,
    x: PropTypes.number,
    y: PropTypes.number,
  };

  static defaultProps = {
    data: [],
    domain: [],
    height: 0,
    layoutMode: LayoutMode.COLUMN_LAYOUT,
    numTicks: 0,
    orient: Orient.BOTTOM,
    tickPadding: 0,
    tickWidth: 0,
    width: 0,
    x: 0,
    y: 0,
  };

  constructor(props) {
    super(props);
    this.onResize = this.onResize.bind(this);
    this.state = {
      frequency: 1,
    };
  }

  componentDidMount() {
    this.observer = new ResizeObserver(debounce(this.onResize, 250));
    this.observer.observe(this._node);

    const {numTicks, width} = this.props;
    const {frequency} = this.state;
    const averageTickWidth = this.getComputedTickWidth(this._textNodes);
    this.adjustTickFrequency(numTicks, width, frequency, averageTickWidth);
  }

  componentDidUpdate() {
    const {numTicks, width} = this.props;
    const {frequency} = this.state;
    const averageTickWidth = this.getComputedTickWidth(this._textNodes);
    this.adjustTickFrequency(numTicks, width, frequency, averageTickWidth);
  }

  componentWillUnmount() {
    this.observer.disconnect();
  }

  getComputedAxisWidth(averageTickWidth, numTicks, frequency) {
    return (averageTickWidth * numTicks) / frequency;
  }

  getComputedTickWidth(textNodes) {
    const nodes = textNodes.filter(identity);

    // this safeguards against filtering down to only 1 x axis label,
    // in which case the x axis would not resize (and not fire onResize)
    if (nodes.length <= 3) {
      return null;
    }

    const computedTickWidths = nodes.map((node) =>
      node.getComputedTextLength(),
    );
    return mean(computedTickWidths);
  }

  getFontSize(width) {
    let fontSize = 10;
    const widths = [820, 760, 680];
    const sizes = [9, 8, 7];
    widths.forEach((datum, index) => {
      if (width <= datum) {
        fontSize = sizes[index];
      }
    });
    return fontSize;
  }

  getOffset(tickWidth, layoutMode) {
    return layoutMode === LayoutMode.COLUMN_LAYOUT
      ? Math.floor(tickWidth / 2)
      : 0;
  }

  getTickX(layoutMode, width, tickWidth, offset, index, numTicks) {
    return layoutMode === LayoutMode.COLUMN_LAYOUT
      ? Math.floor(tickWidth * index + offset)
      : getFullWidthX(width, numTicks, index);
  }

  getTickY(tickPadding, orient, height) {
    return orient === Orient.BOTTOM
      ? Math.ceil(tickPadding)
      : Math.ceil(height / 3);
  }

  adjustTickFrequency(numTicks, width, frequency, averageTickWidth) {
    // if every 50th is still overlapping, they are probably
    // on a tiny screen that we don't support
    if (frequency > 50) {
      return null;
    }

    const overlap = this.hasOverlappingLabels(
      numTicks,
      width,
      frequency,
      averageTickWidth,
    );
    if (overlap) {
      this.incrementTickFrequency(frequency);
    }
  }

  hasOverlappingLabels(numTicks, width, frequency, averageTickWidth) {
    // the first render has width at a negative value
    if (width < 0 || averageTickWidth === null) {
      return false;
    }

    const computedAxisWidth = this.getComputedAxisWidth(
      averageTickWidth,
      numTicks,
      frequency,
    );

    // allow the width of 2 ticks as total amount of space between
    // all ticks to create a bit of space between ticks
    const widthTarget = width - averageTickWidth * 2;
    return computedAxisWidth > widthTarget;
  }

  incrementTickFrequency(frequency) {
    const step = frequency === 1 ? 1 : 2;
    this.setState({
      frequency: frequency + step,
    });
  }

  render() {
    const {frequency} = this.state;
    const fontSize = this.getFontSize(this.props.width);
    const offset = this.getOffset(this.props.tickWidth, this.props.layoutMode);
    const ticks = getTimeTicks(this.props.data, frequency);
    const translate = getTranslate(this.props.x, this.props.y);

    this._textNodes = [];
    return (
      <g
        className={styles.TimeAxis}
        ref={(node) => (this._node = node)}
        transform={translate}>
        {ticks.map((datum, index) => {
          const tickX = this.getTickX(
            this.props.layoutMode,
            this.props.width,
            this.props.tickWidth,
            offset,
            index,
            ticks.length,
          );
          const tickY = this.getTickY(
            this.props.tickPadding,
            this.props.orient,
            this.props.height,
          );
          return datum ? (
            <TimeAxisTickLabel
              fontSize={fontSize}
              key={index}
              text={datum}
              textRef={(node) => this._textNodes.push(node)}
              x={tickX}
              y={tickY}
            />
          ) : null;
        })}
      </g>
    );
  }

  onResize() {
    this.setState({
      frequency: 1,
    });
  }
}
